import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import Select from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Search, Trash2, MapPin } from 'lucide-react';
import { createArea, deleteArea, getAreas, updateArea } from '../../lib/apiService';

const INDIAN_STATES = [
  'Andaman and Nicobar Islands','Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chandigarh',
  'Chhattisgarh','Dadra and Nagar Haveli & Daman and Diu','Delhi','Goa','Gujarat','Haryana',
  'Himachal Pradesh','Jammu and Kashmir','Jharkhand','Karnataka','Kerala','Ladakh','Lakshadweep',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Puducherry',
  'Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand',
  'West Bengal'
];

const AddAreaPage = () => {
  const [form, setForm] = useState({ district: '', state: '' });
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [editForm, setEditForm] = useState({ district: '', state: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [filterState, setFilterState] = useState('Nagaland');

  const fetchAreas = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAreas(filterState);
      setAreas(data?.areas || []);
    } catch {
      setAreas([]);
    } finally {
      setLoading(false);
    }
  }, [filterState]);

  useEffect(() => { fetchAreas(); }, [fetchAreas]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.district.trim() || !form.state) {
      toast.error('District name and state are required');
      return;
    }
    setSubmitting(true);
    try {
      await createArea({ district: form.district.trim(), state: form.state });
      toast.success('District added successfully');
      setForm({ district: '', state: '' });
      fetchAreas();
    } catch (err) {
      toast.error(err.message || 'Failed to add district');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingArea) return;
    if (!editForm.district.trim() || !editForm.state) {
      toast.error('Fill all fields');
      return;
    }
    setSavingEdit(true);
    try {
      await updateArea(editingArea.id, { district: editForm.district.trim(), state: editForm.state });
      toast.success('District updated');
      setEditingArea(null);
      fetchAreas();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (area) => {
    if (!window.confirm(`Delete district "${area.district}, ${area.state}"?`)) return;
    setDeletingId(area.id);
    try {
      await deleteArea(area.id);
      toast.success('District deleted');
      fetchAreas();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = areas;

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Add Area | JoldiPabo Data Entry</title>
      </Helmet>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Location Management
        </h1>
        <p className="text-sm text-slate-600 mt-1">Manage districts and states available for property listings.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px,1fr] gap-6">
        <Card className="p-6 h-fit">
          <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#ee7923]" /> Add District
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>District Name <span className="text-red-500">*</span></Label>
              <Input
                className="mt-1.5 h-11"
                placeholder="e.g. Kohima"
                value={form.district}
                onChange={e => setForm(prev => ({ ...prev, district: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label>State <span className="text-red-500">*</span></Label>
              <div className="mt-1.5">
                <Select
                  options={INDIAN_STATES.map(s => ({ value: s, label: s }))}
                  value={form.state}
                  onChange={v => setForm(prev => ({ ...prev, state: v }))}
                  placeholder="Select state"
                  required
                />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800">
              {submitting ? 'Adding...' : 'Add District'}
            </Button>
          </form>
        </Card>

        <Card className="overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-medium text-slate-900">Registered Districts <span className="text-sm text-slate-500 font-normal ml-2">({areas.length})</span></h2>
            <div className="w-full sm:max-w-xs">
              <Select
                options={[{ label: 'All States', value: '' }, ...INDIAN_STATES.map(s => ({ value: s, label: s }))]}
                value={filterState}
                onChange={v => setFilterState(v)}
                placeholder="Filter by state..."
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">District</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">State</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(area => (
                    <tr key={area.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-3 text-sm font-medium text-slate-900">{area.district}</td>
                      <td className="px-6 py-3 text-sm text-slate-600">{area.state}</td>
                      <td className="px-6 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setEditingArea(area); setEditForm({ district: area.district, state: area.state }); }}
                            className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(area)}
                            disabled={deletingId === area.id}
                            className="h-8 w-8 flex items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-40"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {areas.length === 0 && <div className="py-16 text-center text-slate-500">No districts added yet</div>}
              {areas.length > 0 && filtered.length === 0 && <div className="py-16 text-center text-slate-500">No results found</div>}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={Boolean(editingArea)} onOpenChange={open => { if (!open) setEditingArea(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Edit District</DialogTitle>
            <DialogDescription>Update the district name or state.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>District Name</Label>
              <Input className="mt-1.5 h-11" value={editForm.district} onChange={e => setEditForm(prev => ({ ...prev, district: e.target.value }))} />
            </div>
            <div>
              <Label>State</Label>
              <div className="mt-1.5">
                <Select
                  options={INDIAN_STATES.map(s => ({ value: s, label: s }))}
                  value={editForm.state}
                  onChange={v => setEditForm(prev => ({ ...prev, state: v }))}
                  placeholder="Select state"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingArea(null)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={savingEdit} className="bg-slate-900 hover:bg-slate-800">
              {savingEdit ? 'Saving...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddAreaPage;
